// src/components/PrintLabelModal.js

import React, { useState } from "react";
import { ProductLabel, ScrewPicker } from "./ProductLabel";
import {
  ImplantLabel,
  isImplantProduct,
  todayISO,
  addYears,
  LABEL_WIDTH_MM,
  LABEL_HEIGHT_MM,
} from "./ImplantLabel";

export const PrintLabelModal = ({ product, onClose, needsScrew = false, screwOptions = [] }) => {
  const implant = isImplantProduct(product);

  const [mfgDate, setMfgDate] = useState(todayISO());
  const [expDate, setExpDate] = useState(addYears(todayISO(), 5));
  const [expTouched, setExpTouched] = useState(false);
  const [refText, setRefText] = useState(product?.reference || product?.SKU || "");
  const [lotText, setLotText] = useState(product?.lot_number || "");

  // ✅ Screw selection (Ti-base / Scanbody) — first matching screw is selected by default
  const [screwId, setScrewId] = useState(() =>
    screwOptions.length > 0 ? String(screwOptions[0].product_Id) : ""
  );
  const selectedScrew = screwOptions.find((s) => String(s.product_Id) === screwId);
  const screwSku = needsScrew && selectedScrew ? selectedScrew.SKU : "";

  const implantData = {
    ref: refText,
    lot: lotText,
    name: product?.product_variation?.variation_name || product?.product_Name || "",
    mfgDate,
    expDate,
  };

  const handleMfgChange = (value) => {
    setMfgDate(value);
    if (!expTouched && value) {
      setExpDate(addYears(value, 5));
    }
  };

  // ✅ RELIABLE PRINT HELPER (No iframe, No 1/2 pages)
  const printLabelWindow = (htmlContent) => {
    const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

    if (!printWindow) {
      alert('Print window blocked. Please allow pop-ups for this site to print labels.');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();

        // ✅ Print window ko print ke baad band karne ke liye (Optional but recommended)
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 250);
    };
  };

  const printImplantLabel = () => {
    const svg = document.getElementById("implant-label");
    if (!svg) return;

    const clone = svg.cloneNode(true);
    clone.querySelectorAll(".preview-only").forEach((el) => el.remove());
    clone.setAttribute("width", `${LABEL_WIDTH_MM}mm`);
    clone.setAttribute("height", `${LABEL_HEIGHT_MM}mm`);

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Implant Label</title>
          <style>
            @page { size: ${LABEL_WIDTH_MM}mm ${LABEL_HEIGHT_MM}mm; margin: 0; }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            /* STRICT SIZE FOR BODY */
            html, body { width: ${LABEL_WIDTH_MM}mm !important; height: ${LABEL_HEIGHT_MM}mm !important; margin: 0 !important; padding: 0 !important; background: #fff !important; overflow: hidden !important; }
            svg { display: block !important; width: ${LABEL_WIDTH_MM}mm !important; height: ${LABEL_HEIGHT_MM}mm !important; page-break-after: avoid; break-after: avoid; }
          </style>
        </head>
        <body>${clone.outerHTML}</body>
      </html>
    `;
    printLabelWindow(html);
  };

  const printLabel = (country) => {
    const labelElement = document.getElementById("print-label");
    if (!labelElement) return;

    const ukAddress = `188–190 Lythalls Lane,<br /> Coventry, CV6 6FT, United Kingdom`;
    const pkAddress = `House # 13, Street # 14, Sector C1 -<br /> Bahria Enclave, Islamabad`;

    const labelClone = labelElement.cloneNode(true);

    const addressElements = labelClone.getElementsByClassName("label-address");
    const madeInElements = labelClone.getElementsByClassName("made-in-text");
    const companyElements = labelClone.getElementsByClassName("label-company");

    for (let el of addressElements) el.innerHTML = country === "uk" ? ukAddress : pkAddress;
    for (let el of companyElements) el.textContent = country === "pk" ? "Hira Tech International Solution" : "Hira-Tech Limited";
    for (let el of madeInElements) { if (country === "pk") el.remove(); else el.textContent = "Made in UK"; }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Product Label</title>
          <style>
            /* FORCE EXACT 60mm x 40mm */
            @page { size: 60mm 40mm; margin: 0; }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

            /* STRICT SIZE FOR BODY TO PREVENT FULL PAGE STRETCH */
            html, body {
              width: 60mm !important;
              height: 39.8mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              overflow: hidden !important;
              font-family: Arial, sans-serif;
              color: #000;
            }

            /* WRAPPER FOR LABEL */
            #print-page {
              width: 60mm !important;
              height: 39.8mm !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              overflow: hidden !important;
              page-break-after: avoid;
              break-after: avoid;
              position: relative !important;
            }

            /* LABEL INNER STYLES */
            #print-page #print-label { width: 60mm !important; height: 39.8mm !important; display: flex !important; flex-direction: column !important; justify-content: flex-start !important; margin: 0 !important; padding: 1.5mm 2mm 1mm 2mm !important; box-sizing: border-box !important; overflow: hidden !important; color: #000 !important; background-color: #fff !important; font-family: Arial, sans-serif !important; opacity: 1 !important; }
            #print-page #print-label > div { margin: 0 !important; padding: 0 !important; flex-shrink: 0 !important; }
            #print-page #print-label hr { display: block !important; border: 0 !important; border-top: 1px solid #000 !important; width: 100% !important; height: 1px !important; min-height: 1px !important; margin: 1px 0 2px 0 !important; padding: 0 !important; background: #000 !important; opacity: 1 !important; }
            #print-page #print-label svg { display: inline-block !important; visibility: visible !important; opacity: 1 !important; color: #000 !important; }
            #print-page img { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            #print-page #print-label span { display: inline-block !important; color: #000 !important; }
            #print-page #print-label span[style*="position: absolute"] { position: absolute !important; }
            #print-page #print-label > div:first-child > div:first-child { font-size: 7.5px !important; line-height: 1.05 !important; font-weight: 900 !important; font-family: "Arial Black", Arial, sans-serif !important; display: -webkit-box !important; -webkit-line-clamp: 3 !important; -webkit-box-orient: vertical !important; overflow: hidden !important; }
          </style>
        </head>
        <body>
          <div id="print-page">${labelClone.outerHTML}</div>
        </body>
      </html>
    `;
    printLabelWindow(html);
  };

  const smallInput = { width: "100%", padding: "6px 8px", fontSize: "13px", borderRadius: "5px", border: "1px solid #ccc", boxSizing: "border-box" };
  const smallLabel = { display: "block", textAlign: "left", fontSize: "12px", fontWeight: "bold", marginBottom: "3px" };

  // ✅ Helper to prevent closing when clicking inside modal
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (implant) {
    return (
      <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={handleOverlayClick}>
        <div style={{ width: "100%", maxWidth: "500px", maxHeight: "92vh", overflowY: "auto", backgroundColor: "white", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", borderRadius: "8px", padding: "16px" }} onClick={(e) => e.stopPropagation()}>
          <h3 style={{ margin: "0 0 10px 0", fontSize: "16px" }}>Tyvek Label (65mm × 35mm)</h3>
          <div style={{ width: "450px", maxWidth: "100%", height: "260px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "visible" }}>
            <div style={{ transform: "scale(1.7)", transformOrigin: "center center" }}>
              <ImplantLabel data={implantData} preview />
            </div>
          </div>
          <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "12px" }}>
            <div><label style={smallLabel}>Manufacture date</label><input type="date" value={mfgDate} onChange={(e) => handleMfgChange(e.target.value)} style={smallInput} /></div>
            <div><label style={smallLabel}>Expiry date</label><input type="date" value={expDate} onChange={(e) => { setExpTouched(true); setExpDate(e.target.value); }} style={smallInput} /></div>
            <div><label style={smallLabel}>REF</label><input type="text" value={refText} onChange={(e) => setRefText(e.target.value)} placeholder="e.g. 1001-81-4010" style={smallInput} /></div>
            <div><label style={smallLabel}>LOT</label><input type="text" value={lotText} onChange={(e) => setLotText(e.target.value)} placeholder="e.g. HTP01BL10260705-1" style={smallInput} /></div>
          </div>
          <button onClick={printImplantLabel} style={{ marginTop: "16px", padding: "10px 12px", fontSize: "14px", fontWeight: "bold", color: "white", backgroundColor: "#007BFF", border: "none", borderRadius: "5px", cursor: "pointer", width: "200px", textAlign: "center" }}>Print Tyvek Label</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0, 0, 0, 0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }} onClick={handleOverlayClick}>
      <div style={{ width: "100%", maxWidth: "500px", height: "100%", maxHeight: "560px", backgroundColor: "white", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", borderRadius: "8px", padding: "10px", overflow: "visible" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ width: "450px", height: "300px", display: "flex", justifyContent: "center", alignItems: "center", overflow: "visible" }}>
          <div style={{ transform: "scale(1.8)", transformOrigin: "center center" }}>
            <ProductLabel product={product} type="full" screwSku={screwSku} />
          </div>
        </div>

        {/* Screw dropdown (outside #print-label, so it is never printed) */}
        <ScrewPicker
          product={product}
          needsScrew={needsScrew}
          screwOptions={screwOptions}
          value={screwId}
          onChange={setScrewId}
        />

        <div style={{ display: "flex", gap: "10px", marginTop: needsScrew ? "12px" : "30px" }}>
          <button onClick={() => printLabel("uk")} style={{ padding: "8px 12px", fontSize: "14px", fontWeight: "bold", color: "white", backgroundColor: "#007BFF", border: "none", borderRadius: "5px", cursor: "pointer", width: "150px", textAlign: "center" }}>UK Order Label</button>
          <button onClick={() => printLabel("pk")} style={{ padding: "8px 12px", fontSize: "14px", fontWeight: "bold", color: "white", backgroundColor: "#28a745", border: "none", borderRadius: "5px", cursor: "pointer", width: "150px", textAlign: "center" }}>PK Order Label</button>
        </div>
      </div>
    </div>
  );
};